import { useCallback } from 'react';
import { defaultErrorSubTitle, defaultErrorTitle } from '@/Components/Message/ErrorMessage';
import useSimpleModal from '@/Components/Modal/useSimpleModal';
import { gql, useApolloClient } from '@apollo/client';
import * as SentryBrowser from '@sentry/browser';
import { AppId } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { VIEWING_METHODS_WITH_OFFER } from '@skytvnz/sky-app-store/lib/utils/queries/Fragments';

const GetEpisodeViewingMethod = gql`
  query GetEpisodeWithOnlyViewingMethod($episodeId: ID!, $appId: AppId!) {
    experience(appId: $appId) {
      episode(id: $episodeId) {
        id
        title
        ...viewingMethodWithOffer
      }
    }
  }
  ${VIEWING_METHODS_WITH_OFFER}
`;

const GetShowViewingMethod = gql`
  query GetShowWithOnlyViewingMethod($brandId: ID!, $appId: AppId!) {
    experience(appId: $appId) {
      show(id: $brandId) {
        ... on Show {
          id
          title
          promotedEpisode {
            ...viewingMethodWithOffer
          }
        }
      }
    }
  }
  ${VIEWING_METHODS_WITH_OFFER}
`;

const GetMovieViewingMethod = gql`
  query GetMovieWithOnlyViewingMethod($movieId: ID!, $appId: AppId!) {
    experience(appId: $appId) {
      movie(id: $movieId) {
        id
        title
        ...viewingMethodWithOffer
      }
    }
  }
  ${VIEWING_METHODS_WITH_OFFER}
`;

const GetLinearChannelViewingMethod = gql`
  query GetLinearChannelWithOnlyViewingMethod($channelId: ID!, $appId: AppId!) {
    experience(appId: $appId) {
      linearChannel(id: $channelId) {
        id
        title
        ...viewingMethodWithOffer
      }
    }
  }
  ${VIEWING_METHODS_WITH_OFFER}
`;

const GET_COLLECTION_LINK = gql(/* GraphQL */ `
  query GetCollectionLink($appId: AppId!, $offerId: ID!) {
    experience(appId: $appId) {
      customer {
        offer(id: $offerId) {
          collectionLink {
            collection {
              title
              id
            }
          }
        }
      }
    }
  }
`);

const usePackageOfferDetails = () => {
  const client = useApolloClient();
  const { openModal } = useSimpleModal();

  const handleError = useCallback(
    (error?: any) => {
      openModal({
        title: defaultErrorTitle,
        content: defaultErrorSubTitle,
        onOk: () => {},
      });
      SentryBrowser.captureMessage(`Error while fetching viewing methods ${error}`);
    },
    [openModal],
  );

  const getEpisodeViewingMethods = useCallback(
    async (episodeId: string) => {
      if (!client) {
        handleError();
        return null; // Return null if there is no client
      }
      try {
        const result = await client.query({
          query: GetEpisodeViewingMethod,
          variables: { appId: AppId.SkyGoWeb, episodeId },
          fetchPolicy: 'no-cache',
        });
        // Check for errors in the result
        if (result?.error) {
          handleError(result?.error);
          return null;
        }
        // Check for absence of the expected data
        if (!result?.data?.experience?.episode) {
          handleError();
          return null;
        }
        return result.data.experience.episode.viewingMethods;
      } catch (error) {
        handleError(error);
        return null;
      }
    },
    [client, handleError],
  );

  const getShowViewingMethods = useCallback(
    async (brandId: string) => {
      if (!client) {
        handleError();
        return null; // Return null if there is no client
      }
      try {
        const result = await client.query({
          query: GetShowViewingMethod,
          variables: { appId: AppId.SkyGoWeb, brandId },
          fetchPolicy: 'no-cache',
        });
        // Check for errors in the result
        if (result?.error) {
          handleError(result?.error);
          return null;
        }
        // Check for absence of the expected data
        if (!result?.data?.experience.show) {
          handleError();
          return null;
        }
        return result.data.experience.show?.promotedEpisode?.viewingMethods;
      } catch (error) {
        handleError(error);
        return null;
      }
    },
    [client, handleError],
  );

  const getMovieViewingMethods = useCallback(
    async (movieId: string) => {
      if (!client) {
        handleError();
        return null; // Return null if there is no client
      }

      try {
        const result = await client.query({
          query: GetMovieViewingMethod,
          variables: { appId: AppId.SkyGoWeb, movieId },
          fetchPolicy: 'no-cache',
        });
        // Check for errors in the result
        if (result?.error) {
          handleError(result?.error);
          return null;
        }
        // Check for absence of the expected data
        if (!result?.data?.experience.movie) {
          handleError();
          return null;
        }
        return result.data.experience.movie.viewingMethods;
      } catch (error) {
        handleError(error);
        return null;
      }
    },
    [client, handleError],
  );

  const getLinearChannelViewingMethods = useCallback(
    async (channelId: string) => {
      if (!client) {
        handleError();
        return null; // Return null if there is no client
      }
      try {
        const result = await client.query({
          query: GetLinearChannelViewingMethod,
          variables: { appId: AppId.SkyGoWeb, channelId },
          fetchPolicy: 'no-cache',
        });
        // Check for errors in the result
        if (result?.error) {
          handleError(result?.error);
          return null;
        }
        // Check for absence of the expected data
        if (!result?.data?.experience?.linearChannel) {
          handleError();
          return null;
        }
        return result.data.experience.linearChannel.viewingMethods;
      } catch (error) {
        handleError(error);
        return null;
      }
    },
    [client, handleError],
  );

  const getCollectionLink = useCallback(
    async (offerId: string) => {
      if (!client) {
        handleError();
        return null; // Return null if there is no client
      }
      try {
        const result = await client.query({
          query: GET_COLLECTION_LINK,
          variables: { appId: AppId.SkyGoWeb, offerId },
          fetchPolicy: 'no-cache',
        });
        // Check for errors in the result
        if (result?.error) {
          handleError(result?.error);
          return null;
        }

        return result.data?.experience?.customer?.offer?.collectionLink?.collection?.id;
      } catch (error) {
        handleError(error);
        return null;
      }
    },
    [client, handleError],
  );

  return {
    getCollectionLink,
    getEpisodeViewingMethods,
    getShowViewingMethods,
    getMovieViewingMethods,
    getLinearChannelViewingMethods,
  };
};

export default usePackageOfferDetails;
